import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import RichTextEditor from '../components/RichTextEditor';
import { useToast } from '../components/Toast';
import { useWarnIfUnsaved } from '../context/UnsavedChanges';
import { ContentPage, PageSlug, formatEffectiveDate, getPage, updatePage } from '../api/pages';
import { extractApiError } from '../api/legalServices';

// Each page has its own address and sidebar item (Master Management).
const SLUGS: PageSlug[] = ['terms-of-service', 'privacy-policy', 'delete-account'];

interface Draft {
  title: string;
  effectiveDate: string;
  content: string;
}

/** Copies text, falling back to a hidden textarea where the Clipboard API is unavailable or blocked. */
const copyText = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch {
    // Not a secure context, or clipboard permission denied — try the old way.
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand('copy');
  area.remove();
  if (!ok) throw new Error('Copy failed');
};

interface PageUrlFieldProps {
  page: ContentPage;
  /** Called with the page as saved by the API after the address changes. */
  onSaved: (page: ContentPage) => void;
}

/** The page's public URL, with Copy and an inline editor (a path here or a full URL anywhere). */
const PageUrlField: React.FC<PageUrlFieldProps> = ({ page, onSaved }) => {
  const { toast } = useToast();
  const [editing, setEditing] = useState<boolean>(false);
  const [value, setValue] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(t);
  }, [copied]);

  // Where this site serves the page — still reachable when links point elsewhere.
  const siteAddress = `${window.location.origin}${page.path}`;

  const startEdit = () => {
    setValue(page.url);
    setError(null);
    setEditing(true);
  };

  const copy = async () => {
    try {
      await copyText(page.url);
      setCopied(true);
      toast('Page URL copied', 'success');
    } catch {
      toast('Could not copy — select the URL and copy it manually', 'error');
    }
  };

  const save = async () => {
    const next = value.trim();
    if (next === page.url) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await updatePage(page.slug, { url: next });
      onSaved(saved);
      setEditing(false);
      toast(`${saved.title} now links to ${saved.url}`, 'success');
    } catch (err) {
      setError(extractApiError(err, 'Failed to update the URL'));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="mt-4">
        <label className="form-label" htmlFor="page-url">
          Page URL
        </label>
        <input
          id="page-url"
          className="form-input font-mono text-[13px]"
          style={error ? { borderColor: 'var(--red)' } : undefined}
          value={value}
          maxLength={500}
          autoFocus
          spellCheck={false}
          autoComplete="off"
          placeholder={`https://www.example.com/${page.slug}`}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') setEditing(false);
          }}
        />
        <div className="text-[12px] mt-1.5 leading-relaxed" style={{ color: error ? 'var(--red)' : 'var(--g500)' }}>
          {error || (
            <>
              A full URL on any domain (e.g. https://www.therentagadeapp.com/{page.slug}), or a path
              on this site like <span className="font-mono">/{page.slug}</span>.
            </>
          )}
        </div>
        <div className="flex gap-2 mt-3">
          <button
            className="btn btn-primary btn-sm flex-1 justify-center"
            onClick={save}
            disabled={saving || !value.trim()}
            style={saving || !value.trim() ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
          >
            {saving ? 'Saving...' : 'Save URL'}
          </button>
          <button
            className="btn btn-secondary btn-sm flex-1 justify-center"
            onClick={() => setEditing(false)}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[12px] font-semibold text-g600">Page URL</span>
        {page.externalUrl && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-brand-bg text-brand">
            <Icon name="external-link" size={11} color="var(--brand)" />
            External
          </span>
        )}
      </div>
      <div
        className="px-3.5 py-3 rounded-xl bg-g50 border border-g200 font-mono text-[13px] text-ink select-all leading-relaxed"
        style={{ overflowWrap: 'anywhere' }}
      >
        {/* Prefer wrapping after a slash rather than mid-word. */}
        {page.url.split('/').map((part, i) => (
          <React.Fragment key={i}>
            {i > 0 && '/'}
            {i > 0 && <wbr />}
            {part}
          </React.Fragment>
        ))}
      </div>
      <div className="flex gap-2 mt-2.5">
        <button className="btn btn-secondary btn-sm flex-1 justify-center" onClick={copy}>
          <Icon name={copied ? 'check' : 'copy'} size={14} color={copied ? 'var(--green)' : undefined} />
          {copied ? 'Copied' : 'Copy URL'}
        </button>
        <button className="btn btn-secondary btn-sm flex-1 justify-center" onClick={startEdit}>
          <Icon name="pencil" size={14} />
          Edit URL
        </button>
      </div>
      {page.externalUrl && (
        <div className="text-[12px] text-g500 mt-2 leading-relaxed">
          The website and apps link here. This site still shows the page at{' '}
          <span className="font-mono" style={{ overflowWrap: 'anywhere' }}>{siteAddress}</span> — enter a
          path like <span className="font-mono">{page.path}</span> to link back to it.
        </div>
      )}
    </div>
  );
};

const todayIso = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const PolicyPages: React.FC = () => {
  const { toast } = useToast();
  const { slug: param } = useParams<{ slug: string }>();
  const valid = SLUGS.includes(param as PageSlug);
  const slug: PageSlug = valid ? (param as PageSlug) : 'terms-of-service';
  const [saved, setSaved] = useState<ContentPage | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  // The editor's own rendering of the saved content — what "unchanged" means.
  const [baselineContent, setBaselineContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  // Bumped to remount the editor with fresh content (load, save, discard).
  const [editorKey, setEditorKey] = useState<number>(0);

  const apply = useCallback((page: ContentPage) => {
    setSaved(page);
    setDraft({ title: page.title, effectiveDate: page.effectiveDate || '', content: page.content });
    setBaselineContent(page.content);
    setEditorKey((k) => k + 1);
  }, []);

  const load = useCallback(
    async (target: PageSlug) => {
      setLoading(true);
      setLoadError(null);
      try {
        apply(await getPage(target));
      } catch (err) {
        setLoadError(extractApiError(err, 'Failed to load page'));
      } finally {
        setLoading(false);
      }
    },
    [apply]
  );

  useEffect(() => {
    load(slug);
  }, [slug, load]);

  const dirty =
    !!saved &&
    !!draft &&
    (draft.title.trim() !== saved.title ||
      draft.effectiveDate !== (saved.effectiveDate || '') ||
      draft.content !== baselineContent);

  // Ask before leaving through the sidebar with unsaved edits…
  useWarnIfUnsaved(dirty);

  // …and before closing the tab or reloading.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const contentEmpty = !draft || !draft.content.replace(/<[^>]*>/g, '').trim();
  const canSave = dirty && !saving && !!draft && draft.title.trim() !== '' && !contentEmpty;

  const save = async () => {
    if (!canSave || !draft) return;
    setSaving(true);
    try {
      const page = await updatePage(slug, {
        title: draft.title.trim(),
        effectiveDate: draft.effectiveDate || null,
        content: draft.content,
      });
      apply(page);
      toast(`${page.title} saved — it's live now`, 'success');
    } catch (err) {
      toast(extractApiError(err, 'Failed to save page'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!valid) return <Navigate to="/admin/policies/terms-of-service" replace />;

  return (
    <div>
      {loading ? (
        <div className="card text-center text-g500" style={{ padding: '64px 32px' }}>
          <Icon name="file-text" size={28} color="var(--g400)" /> Loading page...
        </div>
      ) : loadError || !saved || !draft ? (
        <div
          className="card"
          style={{
            padding: 24,
            background: 'var(--red-bg)',
            color: 'var(--red)',
            border: '1px solid rgba(220,38,38,.12)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="x-circle" size={18} color="var(--red)" />
            <strong>Couldn't load this page</strong>
          </div>
          <div className="text-sm mb-3">{loadError}</div>
          <button className="btn btn-secondary btn-sm" onClick={() => load(slug)}>
            Try again
          </button>
        </div>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px] items-start">
          {/* ── Editor ── */}
          <div className="card p-6 animate-fadeIn min-w-0">
            <div className="grid gap-x-5 md:grid-cols-[minmax(0,1fr)_220px]">
              <div className="mb-4">
                <label className="form-label" htmlFor="page-title">
                  Page title
                </label>
                <input
                  id="page-title"
                  className="form-input"
                  value={draft.title}
                  maxLength={150}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  style={draft.title.trim() ? undefined : { borderColor: 'var(--red)' }}
                />
                {!draft.title.trim() && (
                  <div className="text-[12px] mt-1.5" style={{ color: 'var(--red)' }}>
                    A title is required.
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label className="form-label" htmlFor="page-date">
                  Effective date
                </label>
                <input
                  id="page-date"
                  type="date"
                  className="form-input"
                  value={draft.effectiveDate}
                  onChange={(e) => setDraft({ ...draft, effectiveDate: e.target.value })}
                />
                <div className="flex items-center justify-between text-[12px] mt-1.5 text-g500">
                  <span>{draft.effectiveDate ? 'Shown under the title' : 'Not shown'}</span>
                  {draft.effectiveDate !== todayIso() && (
                    <button
                      type="button"
                      className="text-brand font-semibold"
                      onClick={() => setDraft({ ...draft, effectiveDate: todayIso() })}
                    >
                      Set to today
                    </button>
                  )}
                </div>
              </div>
            </div>

            <label className="form-label">Content</label>
            <RichTextEditor
              key={`${slug}-${editorKey}`}
              initialHtml={saved.content}
              contentClassName="legal-prose-numbered"
              onReady={(html) => {
                setBaselineContent(html);
                setDraft((d) => (d ? { ...d, content: html } : d));
              }}
              onChange={(content) => setDraft((d) => (d ? { ...d, content } : d))}
            />
            {contentEmpty && (
              <div className="text-[12px] mt-1.5" style={{ color: 'var(--red)' }}>
                The page can't be empty.
              </div>
            )}

            <div className="flex items-center gap-3 flex-wrap pt-5">
              <button
                className="btn btn-primary"
                onClick={save}
                disabled={!canSave}
                style={canSave ? undefined : { opacity: 0.5, cursor: 'not-allowed' }}
              >
                <Icon name="check-circle" size={16} color="#fff" />
                {saving ? 'Saving...' : 'Save & publish'}
              </button>
              {dirty && !saving && (
                <button className="btn btn-secondary" onClick={() => apply(saved)}>
                  Discard changes
                </button>
              )}
              {dirty && !saving && <span className="text-[13px] text-g500">Unsaved changes</span>}
            </div>
          </div>

          {/* ── Side panel ── */}
          <div className="flex flex-col gap-6">
            <div className="card p-5 animate-fadeIn" style={{ animationDelay: '60ms' }}>
              <div className="flex items-center gap-2.5 mb-4">
                <Icon name="eye" size={18} color="var(--brand)" />
                <span className="text-[15px] font-bold">Published page</span>
              </div>
              <div className="flex flex-col gap-2 text-[13px]">
                <div className="flex justify-between gap-3 px-4 py-3 rounded-xl bg-g50 border border-g100">
                  <span className="text-g600 font-medium">Effective</span>
                  <span className="text-ink font-semibold text-right">
                    {saved.effectiveDate ? formatEffectiveDate(saved.effectiveDate) : 'Not shown'}
                  </span>
                </div>
                <div className="flex justify-between gap-3 px-4 py-3 rounded-xl bg-g50 border border-g100">
                  <span className="text-g600 font-medium">Last saved</span>
                  <span className="text-ink font-semibold text-right">
                    {new Date(saved.updatedAt).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </span>
                </div>
              </div>
              <PageUrlField
                key={slug}
                page={saved}
                // Only the address changed — keep any unsaved edits to the content.
                onSaved={(page) =>
                  setSaved((s) =>
                    s
                      ? {
                          ...s,
                          path: page.path,
                          externalUrl: page.externalUrl,
                          url: page.url,
                          updatedAt: page.updatedAt,
                        }
                      : s
                  )
                }
              />
              <a
                href={saved.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary w-full justify-center mt-2"
              >
                View live page
                <Icon name="external-link" size={14} />
              </a>
            </div>

            <div
              className="flex items-start gap-2.5 px-4 py-3.5 rounded-2xl text-[12.5px] leading-relaxed animate-fadeIn"
              style={{
                background: 'var(--brand-bg)',
                border: '1px solid rgba(225,1,116,0.18)',
                color: 'var(--g700)',
                animationDelay: '120ms',
              }}
            >
              <Icon name="info" size={16} color="var(--brand)" />
              <div className="flex flex-col gap-2">
                <span>
                  Saving publishes immediately to the website and the mobile apps (
                  <code className="font-mono text-[11.5px]">GET /api/pages/{slug}</code>).
                </span>
                <span>
                  The apps read each page's current URL from{' '}
                  <code className="font-mono text-[11.5px]">GET /api/pages/urls</code>, and the
                  website's links update too, so links follow when you edit it.
                </span>
                <span>
                  Use <strong>Section heading</strong> for each main section — they're numbered
                  automatically and listed in the page's Contents box. Use{' '}
                  <strong>Subheading</strong> inside a section.
                </span>
                <span>
                  When you change the policy, update the effective date so users can see it
                  changed.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyPages;
